
import { defineComponent, onBeforeUnmount, onMounted, ref } from "vue";
import { setCurrentPageTitle } from "@/core/helpers/breadcrumb";
import store from "@/store";
import { Booth, Dashboard } from "@/store/enums/StoreEnums";
import moment from "moment";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

export default defineComponent({
  name: "dashboard",
  setup() {
    const totalTransactions = ref(0);
    const totalCounters = ref(0);
    const activeCounters = ref(0);
    const totalServices = ref(0);
    const totalRatings = ref(0);
    const currentMonth = ref(new Date().getMonth() + 1);
    const currentYear = ref(new Date().getFullYear());
    function formatDate(date) {
      const d = date.getDate().toString().padStart(2, "0");
      const m = (date.getMonth() + 1).toString().padStart(2, "0");
      const y = date.getFullYear();
      return `${d}/${m}/${y}`;
    }

    const now = new Date();
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(now.getDate() - 7);

    const search = ref({
      dTuNgay: formatDate(sevenDaysAgo), // 7 days ago
      dDenNgay: formatDate(now),         // today
      counterID: null
    });
    const transactionChartOptions = ref({
      chart: {
        type: 'area',
        toolbar: { show: false },
        fontFamily: 'inherit'
      },
      stroke: { curve: 'smooth', width: 3 },
      xaxis: {
        categories: []
      },
      colors: ['#50CD89', '#F1416C'],
      fill: {
        type: 'gradient',
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.4,
          opacityTo: 0.1,
          stops: [0, 90, 100]
        }
      },
      tooltip: {
        theme: 'dark',
        y: {
          formatter: function (val: number) {
            return val + " lượt"
          }
        }
      }
    });

    const transactionChartSeries = ref([
      {
        name: 'Hoàn thành',
        data: []
      },
      {
        name: 'Từ chối',
        data: []
      }
    ]);

    const ratingChartOptions = {
      chart: {
        type: 'bar',
        toolbar: { show: false },
        fontFamily: 'inherit'
      },
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: '55%',
          // borderRadius: 6,
          dataLabels: {
            position: 'top'
          }
        }
      },
      dataLabels: {
        enabled: true,
        offsetY: -20,
        style: {
          fontSize: '12px',
          colors: ['#304758']
        }
      },
      xaxis: {
        categories: ['Rất hài lòng', 'Hài lòng', 'Bình thường', 'Không hài lòng']
      },
      colors: ['#009EF7'],
      tooltip: {
        theme: 'dark',
        y: {
          formatter: function (val: number) {
            return val + " lượt"
          }
        }
      }
    };

    const ratingChartSeries = ref([
      {
        name: 'Số lượt đánh giá',
        data: []
      }
    ]);

    const counterStats = ref([]);
    const booths = ref([]);
    const loadData = () => {
      store.dispatch(Booth.getBoothList).then(res => {
        if (res.code == 200 && res.data) {
          booths.value = res.data;
        }
      });
      store.dispatch(Dashboard.getCounterStats, {
        columnFilters: {
          tuNgay: { value: search.value.dTuNgay },
          denNgay: { value: search.value.dDenNgay },
          counterID: { value: search.value.counterID }
        }
      }).then(res => {
        if (res.code == 200 && res.data) {
          const counterList = res.data.map((item: any) => {
            return {
              id: item.n_ma_quay,
              name: item.c_ten_quay,
              total: item.luot_lam_viec,
              completed: item.hoan_thanh,
              rejected: item.tu_choi,
              veryHappy: item.rat_hai_long,
              happy: item.hai_long,
              neutral: item.binh_thuong,
              unhappy: item.khong_hai_long
            };
          });
          counterStats.value = counterList;
        }
      });
    };
    const getData = () => {
      store.dispatch(Dashboard.getCounterStats, {
        columnFilters: {
          tuNgay: { value: search.value.dTuNgay },
          denNgay: { value: search.value.dDenNgay },
          counterID: { value: search.value.counterID }
        }
      }).then(res => {
        if (res.code == 200 && res.data) {
          const counterList = res.data.map((item: any) => {
            return {
              id: item.n_ma_quay,
              name: item.c_ten_quay,
              total: item.luot_lam_viec,
              completed: item.hoan_thanh,
              rejected: item.tu_choi,
              veryHappy: item.rat_hai_long,
              happy: item.hai_long,
              neutral: item.binh_thuong,
              unhappy: item.khong_hai_long
            };
          });
          counterStats.value = counterList;
        }
      });
    }
    onMounted(() => {
      setCurrentPageTitle("Dashboard");
      loadData();
    });

    const getTotalSum = (field: string): number => {
      return counterStats.value.reduce((sum: number, counter: any) => sum + (counter[field] || 0), 0);
    };

    const exportToExcel = () => {
      if (counterStats.value.length === 0) {
        alert("Không có dữ liệu để xuất");
        return;
      }

      // Prepare data for export
      const exportData = counterStats.value.map((counter: any) => ({
        "Quầy": counter.name,
        "Tổng lượt": counter.total,
        "Hoàn thành": counter.completed,
        "Từ chối": counter.rejected,
        "Rất hài lòng": counter.veryHappy,
        "Hài lòng": counter.happy,
        "Bình thường": counter.neutral,
        "Không hài lòng": counter.unhappy
      }));

      // Add summary row
      exportData.push({
        "Quầy": "Tổng hợp",
        "Tổng lượt": getTotalSum('total'),
        "Hoàn thành": getTotalSum('completed'),
        "Từ chối": getTotalSum('rejected'),
        "Rất hài lòng": getTotalSum('veryHappy'),
        "Hài lòng": getTotalSum('happy'),
        "Bình thường": getTotalSum('neutral'),
        "Không hài lòng": getTotalSum('unhappy')
      });

      // Create workbook and worksheet
      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Báo cáo quầy");

      // Set column widths
      const colWidths = [
        { wch: 20 },
        { wch: 12 },
        { wch: 12 },
        { wch: 12 },
        { wch: 12 },
        { wch: 12 },
        { wch: 12 },
        { wch: 12 }
      ];
      ws["!cols"] = colWidths;

      // Generate filename
      const timestamp = new Date().toISOString().slice(0, 10);
      const filename = `BaoCaoQuay_${timestamp}.xlsx`;

      // Save file
      XLSX.writeFile(wb, filename);
    };

    return {
      booths,
      search,
      totalTransactions,
      totalCounters,
      activeCounters,
      totalServices,
      totalRatings,
      currentMonth,
      currentYear,
      transactionChartOptions,
      transactionChartSeries,
      ratingChartOptions,
      ratingChartSeries,
      counterStats,
      getData,
      getTotalSum,
      exportToExcel
    };
  }
});
